// Catalogue des modèles : la source unique de tout ce qui vient de l'extérieur du calculateur.
// La veille (scripts/watch-models.ts) fusionne Hugging Face (tendances, licence, popularité),
// Ollama, ModelScope, vLLM, llama.cpp et Epoch AI en un seul fichier, /data/catalog.json,
// réécrit chaque heure. L'identité commune est le dépôt Hugging Face : chaque source y est
// rattachée (ModelScope par son id ou son nom, Ollama par nom et taille, les moteurs par la
// classe d'architecture du config.json, Epoch AI par l'organisation).
//
// La physique (poids, cache KV) reste dans MODELS (llmVram.ts) et ne dépend de rien d'externe.
// Le site lit l'instantané du build (rendu serveur), puis le fichier de l'heure.

import SNAPSHOT from "../data/catalog.json" with { type: "json" };
import { labDef, type Country, type Lab } from "./labs.ts"; // extension : lu aussi par la veille (Node)
import type { ModelPreset } from "./llmVram.ts";

export type CatalogLab = {
  name: string;
  country?: Country;
  /** registry : labs.ts ; epoch : pays trouvé dans Epoch AI ; unknown : pays introuvable. */
  source: "registry" | "epoch" | "unknown";
};

export type CatalogModel = {
  name: string;
  /** Dépôt Hugging Face : l'identité commune à toutes les sources. */
  repo?: string;
  /** Clé de `labs` (organisation Hugging Face en minuscules). */
  lab?: string;
  /** Milliards de paramètres. */
  params?: number;
  /** AAAA-MM-JJ. */
  released?: string;
  license?: string;
  licenseName?: string;
  /** Dépôt à accès restreint sur Hugging Face : la licence est à accepter avant de télécharger (Llama). */
  gated?: boolean;
  /** Fine-tune de ce dépôt d'un autre labo. */
  parent?: string;
  /** false : le calculateur ne sait pas le lire (config inconnue, accès restreint). */
  sizable?: boolean;
  /** Popularité sur Hugging Face (téléchargements des 30 derniers jours). */
  hf?: { likes: number; downloads: number };
  /** Ollama : `ollama run name:tag` ; `pulls` = téléchargements de la famille. */
  /** bytes, projector, quant : taille exacte de cette version (poids, projecteur de vision) et son format,
   *  lus chaque jour dans le registre d'Ollama. */
  ollama?: { name: string; tag?: string; pulls: number; sizes?: string[]; bytes?: number; projector?: number; quant?: string };
  /** Même modèle sur ModelScope. */
  modelscope?: string;
  /** Prise en charge, lue dans les registres officiels ; recipe : page recipes.vllm.ai.
   *  vllmCard : la fiche du modèle documente vLLM (plugin du labo, version précise), même
   *  absent du registre ; vllmSetup : l'installation qu'elle demande avant (`pip install …`). */
  engines?: {
    arch?: string; vllm?: boolean; llamacpp?: boolean; recipe?: string; vllmCard?: boolean; vllmSetup?: string;
    /** Meilleure version GGUF quand le convertisseur de llama.cpp ne connaît pas l'architecture :
     *  arch, celle inscrite dans le fichier ; llamacpp, le moteur de llama.cpp sait la lire. */
    gguf?: { repo: string; arch: string; llamacpp?: boolean };
    /** SGLang (registre de ses modèles, par classe d'architecture) ; MLX : mlx-lm connaît le
     *  model_type (Mac), mlxRepo : la conversion MLX la plus téléchargée. */
    sglang?: boolean; mlx?: boolean; mlxRepo?: string;
  };
  /** Version GGUF la plus téléchargée et la taille exacte de chaque quantification (octets,
   *  fichiers découpés additionnés, projecteur de vision exclu). */
  ggufFiles?: { repo: string; files: Record<string, number> };
  /** Indice de capacités d'Epoch AI (ECI, CC BY 4.0, à créditer) quand Epoch en publie un ; name : le
   *  modèle chez Epoch. Affiché dans le sélecteur de modèles, jamais utilisé pour trier. */
  eci?: { score: number; name: string };
  /** Strata (moteur pour PC de jeu, cache d'experts) quand il fait tourner ce modèle : besoins et vitesses
   *  publiés par le projet (README lu le `checked`), version et étoiles lues chaque jour sur GitHub. */
  strata?: {
    repo: string; checked: string; minVramGB: number; diskGB: number; gpus: string;
    /** « combined » : les paliers de `ram` comptent la RAM et la VRAM ensemble (README de la 0.2.0-beta, 19 octobre
     *  2026). Absent : la RAM seule (catalogue écrit avant). Le texte et le choix de version suivent ce marqueur. */
    memory?: "combined";
    /** Go que le système garde pour lui, retirés de la RAM + VRAM avant de choisir le palier (Strata 0.2.2 : 5). */
    reservedGB?: number;
    /** Comment Strata répartit ce modèle (phrase affichée) ; absent : la description de Qwen3.8-Flash-Next (catalogue d'avant). */
    how?: string;
    /** Longueur du prompt des mesures de lecture (32K pour Qwen3.8-Flash-Next, 8K pour DeepSeek V4.1 Flash). */
    promptTokens?: number;
    ram: { gb: number; pick: string }[];
    speeds: { setup: string; size: string; write: number; writeTo?: number; read: number }[];
    version?: string; released?: string; stars?: number;
  };
  /** Le même modèle par API (Inference Providers de Hugging Face, lu chaque jour) : fournisseurs du moins cher
   *  au plus cher en sortie, avec débit et temps avant le premier token mesurés par Hugging Face. */
  apis?: { checked: string; providers: ApiProvider[] };
  /** Preuves de précision : nos chiffres confrontés à d'autres sources. */
  verified?: {
    /** Cache KV par token d'après les métadonnées du fichier GGUF (ce que llama.cpp allouera). */
    gguf?: { day: string; repo: string; kv: "match" | "differs"; ours: number; theirs: number };
    /** Mesure sur un vrai GPU loué (vLLM) : poids chargés, en GiB, face à notre estimation. */
    gpu?: { day: string; gpu: string; engine: string; format: string; weightsGiB: number; oursGiB: number };
    /** Dernières mesures publiques d'InferenceX (SemiAnalysis, Apache 2.0), une par configuration
     *  (scripts/sources/inferencex.ts) : affichées à côté de notre estimation, jamais à sa place. */
    inferencex?: { model: string; runs: InferenceXRun[] };
  };
  /** Ce que le modèle sait faire et comment le régler, d'après ses fichiers officiels et des bases publiques,
   *  relus chaque jour par la veille (scripts/sources/modelcard.ts). Chaque champ absent : rien de sûr. */
  about?: {
    /** Ce qu'il lit (config.json, type de tâche Hugging Face, OpenRouter). */
    inputs?: ("text" | "image" | "video" | "audio")[];
    /** Appels d'outils : modèle de conversation officiel, ou fournisseurs (OpenRouter). */
    tools?: boolean;
    /** Raisonnement : interrupteur (enable_thinking…), niveaux d'effort (reasoning_effort), toujours (<think>), ou
     *  « supported » : annoncé par les fournisseurs (OpenRouter) sans modèle de conversation pour dire comment. */
    thinking?: "toggle" | "effort" | "always" | "supported";
    /** Niveaux d'effort de raisonnement documentés par le labo (« low », « high »…), quand il les nomme, et où le
     *  choisir quand il le dit (effortVia : « the system prompt ("Reasoning: high") »). */
    efforts?: string[];
    effortVia?: string;
    /** Variable de l'interrupteur du modèle de conversation, à passer dans chat_template_kwargs. */
    switch?: "enable_thinking" | "thinking";
    /** Échantillonnage par défaut : generation_config.json du dépôt (appliqué par vLLM, SGLang et Transformers),
     *  sinon les valeurs par défaut d'OpenRouter. */
    sampling?: { source: "generation_config" | "openrouter" | "card"; temperature?: number; topP?: number; topK?: number; minP?: number; repetitionPenalty?: number };
    /** Langues déclarées par la fiche Hugging Face (ISO 639-1). */
    languages?: string[];
    /** Articles du labo sur ce modèle (arXiv), titres lus sur Hugging Face Papers. */
    papers?: { id: string; title: string; date: string }[];
    /** Fin des connaissances (AAAA-MM ou AAAA-MM-JJ) : fiche officielle, sinon OpenRouter (cutoffFrom). */
    cutoff?: string;
    cutoffFrom?: "card" | "openrouter";
    /** Entraînement selon Epoch AI (CC BY 4.0) : nom chez Epoch, FLOP, tokens, puce et nombre de puces. */
    training?: { name: string; flop?: number; tokens?: number; hardware?: string; chips?: number; confidence?: string };
  };
};

/** Dernière mesure d'une configuration : `gpus` cartes en tensoriel, un serveur. */
export type InferenceXRun = {
  /** Identifiant de la mesure chez InferenceX (son journal : /api/v1/server-log?id=…). */
  id: string;
  day: string; gpu: string; gpus: number; engine: "vllm" | "sglang"; format: string; version?: string;
  /** Journal du serveur : poids et réserve de cache KV par GPU (GiB), tokens qu'elle contient, part de
   *  la mémoire réservée (gpu_memory_utilization, mem_fraction_static). */
  weightsGiB?: number; kvGiB?: number; kvTokens?: number; utilization?: number;
  /** Prompt de 1K, réponse de 1K : tokens/s par requête et temps avant le premier token (s, médianes),
   *  avec `conc` requêtes à la fois (le plus petit nombre mesuré). */
  tps?: number; ttft?: number; conc?: number;
  /** Exécution GitHub Actions de la mesure. */
  url?: string;
};

/** Un fournisseur d'API : dollars par million de tokens (entrée, sortie), tokens/s par requête et temps avant
 *  le premier token (ms) mesurés par Hugging Face, contexte servi. */
export type ApiProvider = { name: string; input: number; output: number; tps?: number; ttftMs?: number; context?: number };

export type Catalog = {
  updated: string;
  labs: Record<string, CatalogLab>;
  /** Clé : id du calculateur s'il a la page du modèle, sinon dépôt Hugging Face, sinon « ollama:nom ». */
  models: Record<string, CatalogModel>;
  /** Clés de `models`, du premier au dernier. */
  rankings: { huggingface: string[]; ollama: string[] };
  /** Dernière lecture réussie de chaque source (ISO). */
  sources: Record<string, string>;
  /** Précision du calculateur face aux démarrages réels publiés par InferenceX (scripts/sources/accuracy.ts) :
   *  mesures comparées, écart médian (%) sur les poids par GPU, le cache KV et la vitesse par requête. */
  accuracy?: { checked: string; runs: number; weightsPct: number; kvPct: number; speedPct: number };
};

// ── Validation : un fichier abîmé est ignoré, l'instantané du build reste affiché ──

const isObj = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const isNum = (v: unknown): v is number => typeof v === "number" && Number.isFinite(v) && v >= 0;
const isStr = (v: unknown): v is string => typeof v === "string";
const opt = <T>(v: unknown, ok: (x: unknown) => x is T) => v === undefined || ok(v);
const REPO = /^[\w.-]+\/[\w.-]+$/;
const isRepo = (v: unknown): v is string => isStr(v) && REPO.test(v);
const isBool = (v: unknown): v is boolean => typeof v === "boolean";
const PIP_LINE = /^(uv )?pip3? install [\w@.:/'"<>=~!,[\] +-]{1,180}$/;
/** Installation lue dans une fiche de modèle et recopiée dans la commande du site (la veille la
 *  filtre avec la même règle) : une ligne `pip install` de paquets publiés, seule, sans
 *  enchaînement de commandes ni installation locale (`-e .`, `-r requirements.txt`, chemin). */
export const isSafeSetup = (line: string) => PIP_LINE.test(line) && !/\s(-e|--editable|-r|--requirement)\b|\s\.{0,2}\/|\s\.{1,2}(\s|$)/.test(line);

const isId = (v: unknown): v is string => isStr(v) && /^[\w.:/-]{1,80}$/.test(v);
const isGiB = (v: unknown): v is number => isNum(v) && v > 0 && v < 100_000;
const isDay = (v: unknown): v is string => isStr(v) && /^\d{4}-\d{2}-\d{2}$/.test(v);

function validApis(v: unknown): boolean {
  return isObj(v) && isDay(v.checked) && Array.isArray(v.providers) && v.providers.length <= 8 && v.providers.every((p) =>
    isObj(p) && isText(p.name, 40) && isNum(p.input) && isNum(p.output) && p.output > 0 && p.output < 1000 &&
    opt(p.tps, isNum) && opt(p.ttftMs, isNum) && opt(p.context, isNum));
}

function validVerified(v: unknown): boolean {
  if (!isObj(v)) return false;
  const { gguf: g, gpu: p, inferencex: x } = v;
  return (g === undefined || (isObj(g) && isDay(g.day) && isRepo(g.repo) && (g.kv === "match" || g.kv === "differs") && isNum(g.ours) && isNum(g.theirs))) &&
    (p === undefined || (isObj(p) && isDay(p.day) && isId(p.gpu) && isId(p.engine) && isId(p.format) && isGiB(p.weightsGiB) && isGiB(p.oursGiB))) &&
    (x === undefined || (isObj(x) && isText(x.model, 60) && Array.isArray(x.runs) && x.runs.length <= 24 && x.runs.every(validRun)));
}
/** Une mesure d'InferenceX : nombres positifs, lien vers GitHub seulement (il devient un href). */
function validRun(r: unknown): r is InferenceXRun {
  return isObj(r) && isStr(r.id) && /^\d{1,12}$/.test(r.id) && isDay(r.day) && isId(r.gpu) && isNum(r.gpus) && r.gpus >= 1 && r.gpus <= 64 &&
    (r.engine === "vllm" || r.engine === "sglang") && isId(r.format) && opt(r.version, (x): x is string => isText(x, 24)) &&
    opt(r.weightsGiB, isGiB) && opt(r.kvGiB, isGiB) && opt(r.kvTokens, isNum) && opt(r.utilization, (x): x is number => isNum(x) && x <= 1) &&
    opt(r.tps, isNum) && opt(r.ttft, isNum) && opt(r.conc, isNum) && opt(r.url, (x): x is string => isStr(x) && x.startsWith("https://github.com/") && x.length < 200);
}

const isText = (v: unknown, max: number): v is string => isStr(v) && v.length > 0 && v.length <= max;
function validStrata(v: unknown): boolean {
  if (!isObj(v)) return false;
  const { ram, speeds } = v;
  return isRepo(v.repo) && isDay(v.checked) && isNum(v.minVramGB) && isNum(v.diskGB) && isText(v.gpus, 300) &&
    Array.isArray(ram) && ram.length <= 8 && ram.every((r) => isObj(r) && isNum(r.gb) && isText(r.pick, 80)) &&
    Array.isArray(speeds) && speeds.length <= 24 &&
    speeds.every((x) => isObj(x) && isText(x.setup, 60) && isText(x.size, 24) && isNum(x.write) && opt(x.writeTo, isNum) && isNum(x.read)) &&
    opt(v.how, (x): x is string => isText(x, 300)) && opt(v.promptTokens, isNum) &&
    opt(v.version, (x): x is string => isText(x, 24)) && opt(v.released, isDay) && opt(v.stars, isNum) &&
    opt(v.memory, (x): x is "combined" => x === "combined") && opt(v.reservedGB, isNum);
}

function validModel(m: unknown): m is CatalogModel {
  if (!isObj(m) || !isStr(m.name)) return false;
  const o = m.ollama, e = m.engines, h = m.hf, gf = m.ggufFiles;
  return opt(m.repo, isRepo) && opt(m.parent, isRepo) && opt(m.lab, isStr) && opt(m.params, isNum) &&
    opt(m.released, isStr) && opt(m.license, isStr) && opt(m.licenseName, isStr) && opt(m.gated, isBool) && opt(m.sizable, isBool) &&
    opt(m.modelscope, isRepo) &&
    (h === undefined || (isObj(h) && isNum(h.likes) && isNum(h.downloads))) &&
    (o === undefined || (isObj(o) && isStr(o.name) && opt(o.tag, isStr) && isNum(o.pulls) &&
      (o.sizes === undefined || (Array.isArray(o.sizes) && o.sizes.every(isStr))) &&
      opt(o.bytes, isNum) && opt(o.projector, isNum) && opt(o.quant, (x): x is string => isStr(x) && /^[\w.-]{1,16}$/.test(x)))) &&
    (e === undefined || (isObj(e) && opt(e.arch, isStr) && opt(e.vllm, isBool) && opt(e.llamacpp, isBool) && opt(e.vllmCard, isBool) &&
      (e.recipe === undefined || (isStr(e.recipe) && e.recipe.startsWith("https://recipes.vllm.ai/"))) &&
      // Recopiée dans la commande de déploiement : une seule installation pip, rien d'autre.
      (e.vllmSetup === undefined || (isStr(e.vllmSetup) && isSafeSetup(e.vllmSetup))) &&
      (e.gguf === undefined || (isObj(e.gguf) && isRepo(e.gguf.repo) && isStr(e.gguf.arch) && opt(e.gguf.llamacpp, isBool))) &&
      opt(e.sglang, isBool) && opt(e.mlx, isBool) && opt(e.mlxRepo, isRepo))) &&
    (gf === undefined || (isObj(gf) && isRepo(gf.repo) && isObj(gf.files) &&
      // Jusqu'à 10 To : Qwen3.8 2.4T en BF16 pèse 4,8 To (refusé entier sous 3 To, le 15 octobre 2026, sa fiche sans licence).
      Object.entries(gf.files).every(([q, b]) => /^[\w-]{2,24}$/.test(q) && isNum(b) && b > 1e8 && b < 1e13))) &&
    (m.eci === undefined || (isObj(m.eci) && isNum(m.eci.score) && m.eci.score < 400 && isStr(m.eci.name))) &&
    (m.strata === undefined || validStrata(m.strata)) &&
    (m.verified === undefined || validVerified(m.verified)) &&
    (m.apis === undefined || validApis(m.apis)) &&
    (m.about === undefined || validAbout(m.about));
}

const isMonthOrDay = (x: unknown): x is string => isStr(x) && /^\d{4}-\d{2}(-\d{2})?$/.test(x);
/** Textes recopiés tels quels dans la page : courts, sans balise. */
const isPlain = (max: number) => (x: unknown): x is string => isStr(x) && x.length > 0 && x.length <= max && !/[<>]/.test(x);
function validAbout(a: unknown): boolean {
  if (!isObj(a)) return false;
  const s = a.sampling, t = a.training, pos = (x: unknown): x is number => isNum(x) && x >= 0 && x < 1e30;
  return (a.inputs === undefined || (Array.isArray(a.inputs) && a.inputs.every((x) => ["text", "image", "video", "audio"].includes(x as string)))) &&
    opt(a.tools, isBool) && (a.thinking === undefined || ["toggle", "effort", "always", "supported"].includes(a.thinking as string)) &&
    (a.switch === undefined || a.switch === "enable_thinking" || a.switch === "thinking") && opt(a.effortVia, isPlain(120)) &&
    (a.efforts === undefined || (Array.isArray(a.efforts) && a.efforts.length <= 6 && a.efforts.every((e) => isStr(e) && /^[a-z-]{2,12}$/.test(e)))) &&
    (s === undefined || (isObj(s) && ["generation_config", "openrouter", "card"].includes(s.source as string) &&
      [s.temperature, s.topP, s.topK, s.minP, s.repetitionPenalty].every((x) => x === undefined || (isNum(x) && x >= 0 && x <= 1000)))) &&
    (a.languages === undefined || (Array.isArray(a.languages) && a.languages.length <= 300 && a.languages.every((l) => isStr(l) && /^[a-z]{2}$/.test(l)))) &&
    (a.papers === undefined || (Array.isArray(a.papers) && a.papers.length <= 5 &&
      a.papers.every((p) => isObj(p) && isStr(p.id) && /^\d{4}\.\d{4,5}$/.test(p.id) && isPlain(300)(p.title) && isMonthOrDay(p.date)))) &&
    opt(a.cutoff, isMonthOrDay) && (a.cutoffFrom === undefined || a.cutoffFrom === "card" || a.cutoffFrom === "openrouter") &&
    (t === undefined || (isObj(t) && isPlain(120)(t.name) && opt(t.flop, pos) && opt(t.tokens, pos) && opt(t.hardware, isPlain(120)) &&
      opt(t.chips, pos) && opt(t.confidence, isPlain(40))));
}

export function validateCatalog(d: unknown): Catalog | null {
  if (!isObj(d) || !isStr(d.updated) || !isObj(d.labs) || !isObj(d.models) || !isObj(d.rankings)) return null;
  const labs: Catalog["labs"] = {};
  for (const [k, l] of Object.entries(d.labs)) {
    if (isObj(l) && isStr(l.name) && opt(l.country, (c): c is string => isStr(c) && /^[A-Z]{2}$/.test(c))) labs[k] = l as CatalogLab;
  }
  const models: Catalog["models"] = {};
  for (const [k, m] of Object.entries(d.models)) if (validModel(m)) models[k] = m;
  const list = (v: unknown) => (Array.isArray(v) ? v.filter((x): x is string => isStr(x) && x in models) : []);
  return {
    updated: d.updated, labs, models,
    rankings: { huggingface: list(d.rankings.huggingface), ollama: list(d.rankings.ollama) },
    sources: isObj(d.sources) ? Object.fromEntries(Object.entries(d.sources).filter(([, v]) => isStr(v))) as Record<string, string> : {},
    ...(validAccuracy(d.accuracy) ? { accuracy: d.accuracy } : {}),
  };
}

const validAccuracy = (a: unknown): a is NonNullable<Catalog["accuracy"]> =>
  isObj(a) && isStr(a.checked) && isNum(a.runs) && a.runs >= 1
  && [a.weightsPct, a.kvPct, a.speedPct].every((x) => isNum(x) && x < 100);

/** Instantané du build (rendu serveur, et repli si le fichier de l'heure est illisible). */
export const CATALOG_SNAPSHOT: Catalog = validateCatalog(SNAPSHOT) ?? {
  updated: "1970-01-01T00:00:00Z", labs: {}, models: {}, rankings: { huggingface: [], ollama: [] }, sources: {},
};

// ── Labos ─────────────────────────────────────────────────────────────────────

/** Labo d'une organisation Hugging Face : le registre (labs.ts) d'abord, puis le catalogue. */
export function labOfOrg(org: string, catalog: Catalog = CATALOG_SNAPSHOT): Lab | undefined {
  const def = labDef(org);
  if (def) return { name: def.name, country: def.country };
  const found = catalog.labs[org.toLowerCase()];
  return found ? { name: found.name, ...(found.country ? { country: found.country } : {}) } : undefined;
}

/** Labo d'un modèle du calculateur (dépôt, ou organisation d'une preview sans dépôt). */
export const labOf = (m: Pick<ModelPreset, "repo" | "org">, catalog?: Catalog) =>
  labOfOrg((m.repo || m.org || "").split("/")[0], catalog);

// ── Licences et nombres ───────────────────────────────────────────────────────

const LICENSES: Record<string, string> = {
  mit: "MIT", "apache-2.0": "Apache 2.0", "bsd-3-clause": "BSD 3-Clause", "cc-by-4.0": "CC BY 4.0",
  "cc-by-nc-4.0": "CC BY-NC 4.0", "cc-by-nc-sa-4.0": "CC BY-NC-SA 4.0", openrail: "OpenRAIL",
  "llama3.1": "Llama 3.1 Community License", "llama3.2": "Llama 3.2 Community License",
  llama3: "Llama 3 Community License", "llama3.3": "Llama 3.3 Community License", llama4: "Llama 4 Community License",
  gemma: "Gemma Terms of Use",
  // Licences maison (license: other + license_name) : leur nom officiel, pas l'identifiant.
  "glm-5.3": "GLM-5.3 License", "kimi-k3": "Kimi K3 License", "modified-mit": "Modified MIT",
  "qwen-community-1.0": "Qwen Community License", "nvidia-nemotron-open-model-license": "NVIDIA Open Model License",
  "lfm1.0": "LFM Open License",
};
/** Licences sans restriction d'usage commercial. */
const PERMISSIVE = new Set(["mit", "apache-2.0", "bsd-3-clause", "cc-by-4.0"]);

/** Licence lisible et si l'usage commercial est libre. Licence maison : son nom officiel s'il est connu, sinon
 *  son identifiant mis en forme (« qwen-research » → « Qwen Research license »), même sans le champ license
 *  (Qwen2.5-VL ne déclare que license_name). */
export function licenseInfo(license?: string, name?: string): { label: string; permissive: boolean } | undefined {
  const id = license?.toLowerCase(), named = name?.toLowerCase();
  if (!id && !named) return undefined;
  const pretty = named && `${named.split("-").map((w) => (/^\d/.test(w) ? w : w.charAt(0).toUpperCase() + w.slice(1))).join(" ")} license`;
  const label = (id && LICENSES[id]) || (named && LICENSES[named]) || pretty || "Custom license";
  return { label, permissive: !!id && PERMISSIVE.has(id) };
}

/** 1 160 332 → « 1.2M », 4 167 → « 4.2K ». */
export const compact = (n: number) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M` : n >= 1e3 ? `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}K` : String(n);

/**
 * Moteurs qui font tourner le modèle : les registres de vLLM et de llama.cpp, plus ce qu'ils ne
 * voient pas. viaCard : vLLM documenté par la fiche du modèle (setup : l'installation à faire
 * avant). gguf : une version GGUF, que llama.cpp lit (runs) ou pas encore (llama.cpp modifié).
 */
export function runsWith(e?: CatalogModel["engines"]):
  { names: string[]; viaCard: boolean; setup?: string; gguf?: { repo: string; arch: string; runs: boolean }; mlxRepo?: string } | undefined {
  if (!e || (e.vllm === undefined && e.llamacpp === undefined && !e.vllmCard && !e.gguf && e.sglang === undefined && e.mlx === undefined)) return undefined;
  const viaCard = !e.vllm && !!e.vllmCard;
  const gguf = !e.llamacpp && e.gguf ? { repo: e.gguf.repo, arch: e.gguf.arch, runs: e.gguf.llamacpp === true } : undefined;
  const names = [
    (e.vllm || viaCard) && "vLLM", e.sglang && "SGLang", (e.llamacpp || gguf?.runs) && "llama.cpp (Ollama, LM Studio)",
    (e.mlx || e.mlxRepo) && "MLX (Mac)",
  ].filter((x): x is string => !!x);
  return { names, viaCard, ...(viaCard && e.vllmSetup ? { setup: e.vllmSetup } : {}), ...(gguf ? { gguf } : {}), ...(e.mlxRepo ? { mlxRepo: e.mlxRepo } : {}) };
}

/** Formats GGUF du calculateur → nom de la quantification dans les fichiers publiés. */
export const GGUF_QUANTS: Record<string, string> = {
  q8_0: "Q8_0", q6_k: "Q6_K", q5_k_m: "Q5_K_M", q4_k_m: "Q4_K_M", q3_k_m: "Q3_K_M", q2_k: "Q2_K", iq2_xxs: "IQ2_XXS",
};

/** Taille exacte des fichiers d'un format GGUF du calculateur, si une version publiée l'a. IQ2_XXS : 16 dépôts sur
 *  19 ne le publient que dans la version dynamique d'Unsloth (UD-IQ2_XXS), prise alors ; deployCommand fait de même. */
export function ggufSize(entry: CatalogModel | undefined, formatId: string): { bytes: number; repo: string; quant: string } | undefined {
  const files = entry?.ggufFiles, base = GGUF_QUANTS[formatId];
  const quant = base && files && formatId === "iq2_xxs" && !files.files[base] && files.files[`UD-${base}`] ? `UD-${base}` : base;
  const bytes = quant && files ? files.files[quant] : undefined;
  return bytes && files ? { bytes, repo: files.repo, quant } : undefined;
}

/** Nom sans suffixe de variante : « Qwen3.7-32B-Instruct-2607 » → « Qwen3.7-32B », « MiMo-V2.6-Pro-MOPD »
 *  → « MiMo-V2.6-Pro » (même modèle, autre post-entraînement). Partagé par la veille (variantes
 *  d'une même sortie) et le site (noms affichés). */
export const baseModelName = (name: string) =>
  name.replace(/-(Instruct|Thinking|Reasoning|Chat|it|hf|BF16|RL|MOPD)(?=-|$)/gi, "").replace(/-\d{4}(?=-|$)/g, "");

/**
 * Nom affiché d'un modèle d'après son dépôt : sans suffixe de variante ni date, la taille
 * détachée comme dans les noms des labos (« Qwen3.6-35B-A3B-Instruct-2507 » → « Qwen3.6 35B-A3B »,
 * « gpt-oss-120b » → « gpt-oss 120B »). Le reste du nom est celui du labo, casse et tirets
 * compris (« GLM-5.3 », « Kolibri-1 »). La même règle pour les modèles publiés par la veille
 * et pour ceux qu'on affiche sans fiche.
 */
export const prettyModelName = (name: string) =>
  baseModelName(name).replace(/-(\d+(?:\.\d+)?)b(?=-A\d|-|$)/gi, " $1B").replace(/-A(\d+(?:\.\d+)?)b(?=-|$)/gi, "-A$1B");

/** Strata : la version que le projet conseille pour cette mémoire (la ligne la plus haute qu'elle atteint),
 *  undefined sous le minimum. memoryGB : RAM + VRAM si `memory` vaut « combined », la RAM seule sinon (strataMemory). */
export const strataPick = (st: NonNullable<CatalogModel["strata"]>, ramGB: number) =>
  [...st.ram].sort((a, b) => a.gb - b.gb).filter((r) => r.gb <= ramGB).at(-1);
/** Strata : la mémoire que comptent ses paliers, pour un poste donné. */
export const strataMemory = (st: NonNullable<CatalogModel["strata"]>, ramGB: number, vramGB: number) =>
  st.memory === "combined" ? ramGB + vramGB - (st.reservedGB ?? 0) : ramGB;
/** Strata : une vitesse publiée, en fourchette quand le projet en donne une (« 6 to 9 »). */
export const strataWrite = (x: { write: number; writeTo?: number }) => (x.writeTo ? `${x.write} to ${x.writeTo}` : `${x.write}`);
/** Strata : comment il répartit le modèle ; catalogue d'avant (sans how) : Qwen3.8-Flash-Next, le seul alors. */
export const strataHow = (st: NonNullable<CatalogModel["strata"]>) => st.how ??
  "Your graphics card keeps the experts used most often, your RAM holds all of them and the processor runs the rest; the n-gram lookup table stays on the SSD.";
/** Strata : la mesure la plus rapide publiée par le projet. */
export const strataFastest = (st: NonNullable<CatalogModel["strata"]>) => [...st.speeds].sort((a, b) => b.write - a.write)[0];

/** `ollama run qwen3.8:27b` */
export const ollamaCommand = (o: NonNullable<CatalogModel["ollama"]>) => `ollama run ${o.name}${o.tag ? `:${o.tag}` : ""}`;
